import { FilterList } from '@mui/icons-material';
import { Box, Menu, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { Button, ButtonVariants } from '../Button';
import { Option } from './Option';

type FilterOption = {
  type: 'radio' | 'checkbox';
  label: string;
  values: string[];
};

export const Filter = ({
  options,
  selectedOptions,
  onFilterChange,
}: {
  options: FilterOption[];
  selectedOptions: (string | string[])[];
  onFilterChange: (selectedOptions: (string | string[])[]) => void;
}) => {
  const ref = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isOptionChanged, setIsOptionChanged] = useState(false);
  const [internalSelectedOptions, setInternalSelectedOptions] =
    useState<(string | string[])[]>(selectedOptions);

  useEffect(() => {
    setInternalSelectedOptions(selectedOptions);
  }, [selectedOptions]);

  return (
    <Box>
      <Button
        ref={ref}
        label="Filter"
        skinny={false}
        variant={ButtonVariants.CUSTOM}
        sx={[
          {
            color: isOptionChanged ? 'secondary.main' : 'primary.main',
            bgcolor: isOptionChanged ? 'secondary.medium' : 'surface.base',
            '&:hover': {
              opacity: 0.75,
            },
            '&:active': {
              bgcolor: 'secondary.medium',
              color: 'secondary.main',
            },
          },
        ]}
        startIcon={<FilterList />}
        onClick={() => setIsOpen(!isOpen)}
      />
      <Menu
        anchorEl={ref.current}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: 384,
              p: 2,
              borderRadius: 1.5,
            },
          },
          list: {
            sx: {
              p: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
            },
          },
        }}
        open={isOpen}
        onClose={() => {
          setInternalSelectedOptions(selectedOptions);
          setIsOpen(false);
        }}>
        <Typography variant="h5">Filters</Typography>
        {options.map((option, index) => (
          <Box key={index}>
            <Typography variant="eyebrowHeader2" sx={{ color: 'primary.main' }}>
              {option.label}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.25,
                mt: 2,
              }}>
              {option.values.map((value, valueIndex) => (
                <Option
                  key={valueIndex}
                  type={option.type}
                  option={value}
                  onClick={selectedOption => {
                    if (option.type === 'checkbox') {
                      if (
                        (internalSelectedOptions[index] as string[]).includes(
                          selectedOption,
                        )
                      ) {
                        setInternalSelectedOptions(prev =>
                          prev.map((opt, i) =>
                            i === index
                              ? (opt as string[]).filter(
                                  v => v !== selectedOption,
                                )
                              : opt,
                          ),
                        );
                      } else {
                        setInternalSelectedOptions(prev =>
                          prev.map((opt, i) =>
                            i === index
                              ? [...(opt as string[]), selectedOption]
                              : opt,
                          ),
                        );
                      }
                    } else {
                      setInternalSelectedOptions(prev =>
                        prev.map((opt, i) =>
                          i === index ? selectedOption : opt,
                        ),
                      );
                    }
                  }}
                  isSelected={
                    option.type === 'checkbox'
                      ? (internalSelectedOptions[index] as string[])?.includes(
                          value,
                        )
                      : internalSelectedOptions[index] === value
                  }
                />
              ))}
            </Box>
            {index < options.length - 1 && (
              <Box
                sx={{ mt: 3, height: '1px', width: '100%', bgcolor: 'grey100' }}
              />
            )}
          </Box>
        ))}
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
          <Button
            variant={ButtonVariants.SECONDARY}
            label="Reset"
            onClick={() => {
              const defaultSelectedOptions = options.map(option =>
                option.type === 'checkbox' ? [] : option.values[0],
              );
              onFilterChange(defaultSelectedOptions);
              setInternalSelectedOptions(defaultSelectedOptions);
              setIsOpen(false);
              setIsOptionChanged(false);
            }}
          />
          <Button
            variant={ButtonVariants.PRIMARY}
            label="Save"
            onClick={() => {
              onFilterChange(internalSelectedOptions);
              setIsOptionChanged(true);
              setIsOpen(false);
            }}
          />
        </Box>
      </Menu>
    </Box>
  );
};
